import React, { useMemo, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/fr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarView.css';
import { Badge, Modal } from 'react-bootstrap';
import { manutentionResume } from './manutentionResume';

// Configure moment with French locale
moment.locale('fr');
const localizer = momentLocalizer(moment);

const CalendarView = ({ orders, onEventClick }) => {
  // Mémoriser la vue du calendrier (mois, semaine, jour, agenda)
  const [calendarView, setCalendarView] = useState(() => {
    return localStorage.getItem('calendarView') || 'month';
  });

  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleViewChange = (view) => {
    setCalendarView(view);
    localStorage.setItem('calendarView', view);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  // Transform orders into calendar events
  const events = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    return orders.map(order => {
      const deliveryDate = order.DateLivraison ? new Date(order.DateLivraison) : new Date();
      // Ajouter 30 minutes à la date de fin pour avoir une hauteur visible
      const endDate = new Date(deliveryDate.getTime() + 60 * 30 * 1000);

      // Determine color based on status
      let statusColor = '#6c757d'; // default gray
      let statusLabel = '';

      switch (order.StatuCommande) {
        case 1: // En cours de livraison
          statusColor = '#ffc107'; // warning
          statusLabel = 'En livraison';
          break;
        case 2: // Livré
          statusColor = '#198754'; // success
          statusLabel = 'Livré';
          break;
        case 3: // En préparation
          statusColor = '#0dcaf0'; // info
          statusLabel = 'En préparation';
          break;
        case 4: // En validation
          statusColor = '#0d6efd'; // primary
          statusLabel = 'En validation';
          break;
        case 6: // Annulé
          statusColor = '#dc3545'; // danger
          statusLabel = 'Annulé';
          break;
        default:
          statusLabel = 'Inconnu';
      }

      const villeLivraison = order.AdresseLivraison?.split(',')[1]?.trim() || '—';
      const villePickup = order.AdressePickUp?.split(',')[1]?.trim() || '—';
      const fournisseur = order.fournisseur_profil?.user_profil?.RaisonSocial || '—';
      const adressePickup = order.AdressePickUp || 'Non renseignée';
      const adresseLivraison = order.AdresseLivraison || 'Non renseignée';

      // Determine client type icon and label
      let clientTypeIcon = '';
      let clientTypeLabel = '';
      let clientTypeColor = '';

      switch (order.client_type) {
        case 'fournisseur':
          clientTypeIcon = '🏢';
          clientTypeLabel = 'Fournisseur';
          clientTypeColor = '#0d6efd';
          break;
        case 'particulier':
          clientTypeIcon = '👤';
          clientTypeLabel = 'Particulier';
          clientTypeColor = '#198754';
          break;
        case 'entreprise':
          clientTypeIcon = '💼';
          clientTypeLabel = 'Entreprise';
          clientTypeColor = '#ffc107';
          break;
        default:
          clientTypeIcon = '❓';
          clientTypeLabel = 'Inconnu';
          clientTypeColor = '#6c757d';
      }

      return {
        id: order.id,
        title: `#${order.id} - ${fournisseur}`,
        start: deliveryDate,
        end: endDate,
        resource: {
          order,
          statusColor,
          statusLabel,
          villeLivraison,
          villePickup,
          fournisseur,
          adressePickup,
          adresseLivraison,
          clientTypeIcon,
          clientTypeLabel,
          clientTypeColor,
        },
      };
    });
  }, [orders]);

  // Custom event style getter
  const eventStyleGetter = (event) => {
    const style = {
      backgroundColor: event.resource.statusColor,
      borderRadius: '4px',
      opacity: 0.9,
      color: 'white',
      border: '0px',
      display: 'block',
      fontSize: '0.85rem',
      padding: '2px 5px',
    };
    return { style };
  };

  // Handle event selection
  const handleSelectEvent = (event) => {
    if (onEventClick) {
      onEventClick(event.resource.order);
    } else {
      // Navigate to order details by default
      window.location.href = `/commande-fournisseur/show/${event.id}`;
    }
  };

  // Custom event component for better display
  const CustomEvent = ({ event }) => {
    const order = event.resource.order;
    const manutention = manutentionResume(order);
    return (
    <div className="calendar-event-content">
      <div>
        {/* Icônes seules : la case est petite. Le détail est dans l'infobulle. */}
        <strong>#{event.id}</strong> {event.resource.clientTypeIcon}
        {order.manutention_deux_personnes && <span title="2 livreurs (colis > 40 kg)"> 👥</span>}
        {manutention && <span title={`Manutention : ${manutention.libelle}`}> 🏋️</span>}
      </div>
      <div style={{ fontSize: '0.75rem' }}>
        📍 {event.resource.villePickup} → 🚚 {event.resource.villeLivraison}
      </div>
      <div className="calendar-event-tooltip">
        <div className="tooltip-row">
          <strong>Type:</strong>
          <span>{event.resource.clientTypeIcon} {event.resource.clientTypeLabel}</span>
        </div>
        <div className="tooltip-row">
          <strong>📍 Pickup:</strong>
          <span>{event.resource.adressePickup}</span>
        </div>
        <div className="tooltip-row">
          <strong>🚚 Livraison:</strong>
          <span>{event.resource.adresseLivraison}</span>
        </div>
        {order.manutention_deux_personnes && (
          <div className="tooltip-row">
            <strong>👥 Équipe:</strong>
            <span>2 livreurs (colis &gt; 40 kg)</span>
          </div>
        )}
        {manutention && (
          <div className="tooltip-row">
            <strong>🏋️ Manutention:</strong>
            <span>{manutention.libelle}</span>
          </div>
        )}
      </div>
    </div>
    );
  };

  // Messages in French
  const messages = {
    allDay: 'Journée',
    previous: 'Précédent',
    next: 'Suivant',
    today: "Aujourd'hui",
    month: 'Mois',
    week: 'Semaine',
    day: 'Jour',
    agenda: 'Agenda',
    date: 'Date',
    time: 'Heure',
    event: 'Événement',
    noEventsInRange: 'Aucune livraison prévue pour cette période',
    showMore: (total) => `+ ${total} de plus`,
  };

  return (
    <>
      <div className="calendar-view">
        <div className="calendar-header mb-3">
          <div className="calendar-legend">
            <div className="mb-2">
              <strong>Statuts:</strong>
              <span className="ms-2 me-3">
                <Badge bg="primary">En validation</Badge>
              </span>
              <span className="me-3">
                <Badge bg="info">En préparation</Badge>
              </span>
              <span className="me-3">
                <Badge bg="warning">En livraison</Badge>
              </span>
              <span className="me-3">
                <Badge bg="success">Livré</Badge>
              </span>
              <span className="me-3">
                <Badge bg="danger">Annulé</Badge>
              </span>
            </div>
            <div>
              <strong>Types:</strong>
              <span className="ms-2 me-3">
                🏢 Fournisseur
              </span>
              <span className="me-3">
                👤 Particulier
              </span>
              <span className="me-3">
                💼 Entreprise
              </span>
            </div>
          </div>
          <button
            className="calendar-zoom-btn"
            onClick={toggleFullscreen}
            title="Plein écran"
          >
            <span>⛶ Plein écran</span>
          </button>
        </div>

        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 500 }}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={handleSelectEvent}
          messages={messages}
          components={{
            event: CustomEvent,
          }}
          views={['month', 'week', 'day', 'agenda']}
          view={calendarView}
          onView={handleViewChange}
          popup
          min={new Date(1970, 1, 1, 7, 0, 0)}
          max={new Date(1970, 1, 1, 18, 0, 0)}
        />
      </div>

      {/* Modal plein écran */}
      <Modal
        show={isFullscreen}
        onHide={toggleFullscreen}
        size="xl"
        fullscreen
        className="calendar-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Calendrier des livraisons</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="calendar-legend mb-3">
            <div className="mb-2">
              <strong>Statuts:</strong>
              <span className="ms-2 me-3">
                <Badge bg="primary">En validation</Badge>
              </span>
              <span className="me-3">
                <Badge bg="info">En préparation</Badge>
              </span>
              <span className="me-3">
                <Badge bg="warning">En livraison</Badge>
              </span>
              <span className="me-3">
                <Badge bg="success">Livré</Badge>
              </span>
              <span className="me-3">
                <Badge bg="danger">Annulé</Badge>
              </span>
            </div>
            <div>
              <strong>Types:</strong>
              <span className="ms-2 me-3">
                🏢 Fournisseur
              </span>
              <span className="me-3">
                👤 Particulier
              </span>
              <span className="me-3">
                💼 Entreprise
              </span>
            </div>
          </div>

          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 'calc(100vh - 250px)' }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleSelectEvent}
            messages={messages}
            components={{
              event: CustomEvent,
            }}
            views={['month', 'week', 'day', 'agenda']}
            view={calendarView}
            onView={handleViewChange}
            popup
            min={new Date(1970, 1, 1, 7, 0, 0)}
            max={new Date(1970, 1, 1, 18, 0, 0)}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CalendarView;
